import { CircleAlert, CircleCheck, Info, Loader2, TriangleAlert } from 'lucide-react';
import type { HTMLAttributes, ReactNode } from 'react';
import { initials } from '../../lib/format.ts';
import { usePageTitle } from '../../lib/navigation.ts';
import { BackButton } from './back.tsx';

export function Card({ className = '', ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={`rounded-xl border border-line bg-surface shadow-card ${className}`} {...props} />;
}

export function Spinner({ label = 'Loading…', className = '' }: { label?: string; className?: string }) {
  return (
    <div role="status" className={`flex items-center justify-center gap-2 py-10 text-muted ${className}`}>
      <Loader2 className="size-5 animate-spin" aria-hidden />
      <span className="text-sm">{label}</span>
    </div>
  );
}

const ALERT_TONES = {
  error: { cls: 'bg-danger-soft text-danger', Icon: CircleAlert },
  warning: { cls: 'bg-warning-soft text-warning', Icon: TriangleAlert },
  info: { cls: 'bg-info-soft text-info', Icon: Info },
  success: { cls: 'bg-brand-soft text-brand-ink', Icon: CircleCheck },
};

export function Alert({
  tone = 'info',
  title,
  children,
  className = '',
}: {
  tone?: keyof typeof ALERT_TONES;
  title?: ReactNode;
  children?: ReactNode;
  className?: string;
}) {
  const { cls, Icon } = ALERT_TONES[tone];
  return (
    <div
      role={tone === 'error' ? 'alert' : undefined}
      className={`flex gap-3 rounded-lg p-3.5 ${cls} ${className}`}
    >
      <Icon className="mt-0.5 size-5 shrink-0" aria-hidden />
      <div className="min-w-0 text-sm">
        {title && <p className="font-semibold">{title}</p>}
        {children && <div className={title ? 'mt-0.5' : ''}>{children}</div>}
      </div>
    </div>
  );
}

export function EmptyState({
  icon,
  title,
  children,
  action,
}: {
  icon: ReactNode;
  title: string;
  children?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center px-6 py-12 text-center">
      <div className="mb-3 flex size-12 items-center justify-center rounded-full bg-surface-2 text-muted">
        {icon}
      </div>
      <p className="font-semibold">{title}</p>
      {children && <div className="mt-1 max-w-sm text-sm text-muted">{children}</div>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

/**
 * The top of every page: title (also used for the browser tab), and a Back
 * button on pages that aren't a main destination. `back` is where Back
 * goes when the visit started on this page.
 */
export function PageHeader({
  title,
  description,
  actions,
  back,
  confirmLeave,
}: {
  title: string;
  description?: ReactNode;
  actions?: ReactNode;
  back?: string;
  confirmLeave?: () => boolean;
}) {
  usePageTitle(title);
  return (
    <div className="mb-6">
      {back && (
        <div className="mb-2">
          <BackButton to={back} confirmLeave={confirmLeave} />
        </div>
      )}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold tracking-tight">{title}</h1>
          {description && <p className="mt-1 text-muted">{description}</p>}
        </div>
        {actions && <div className="flex gap-2">{actions}</div>}
      </div>
    </div>
  );
}

export function Avatar({ name, className = '' }: { name: string; className?: string }) {
  return (
    <span
      aria-hidden
      className={`inline-flex size-8 shrink-0 items-center justify-center rounded-full bg-brand-soft text-xs font-semibold text-brand-ink ${className}`}
    >
      {initials(name)}
    </span>
  );
}
